"use client";

import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  FONT_MONO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
} from "@/components/ui/organisms/chord-practice/palette";
import { useChordLabel, useNoteNotation } from "@/hooks/use-notation";
import { noteLabel, type NoteNotation } from "@/lib/music/notation";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { AUTO_ADVANCE_TIMINGS } from "./use-auto-advance";

const SHARP_NAMES = [
  "C",
  "C♯",
  "D",
  "D♯",
  "E",
  "F",
  "F♯",
  "G",
  "G♯",
  "A",
  "A♯",
  "B",
];
const FLAT_NAMES = [
  "C",
  "D♭",
  "D",
  "E♭",
  "E",
  "F",
  "G♭",
  "G",
  "A♭",
  "A",
  "B♭",
  "B",
];

function midiToNoteLabel(
  midi: number,
  useFlats: boolean,
  notation: NoteNotation,
): string {
  const pc = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const names = useFlats ? FLAT_NAMES : SHARP_NAMES;

  return noteLabel(`${names[pc]}${octave}`, notation);
}

function midiToPitchClass(
  midi: number,
  useFlats: boolean,
  notation: NoteNotation,
): string {
  const pc = ((midi % 12) + 12) % 12;
  const names = useFlats ? FLAT_NAMES : SHARP_NAMES;

  return noteLabel(names[pc]!, notation);
}

export interface TransitionSlot {
  roman: string;
  symbol: string;
  /** Function colour (T/SD/D), used to tint the roman numeral. */
  functionColor: string;
  /** Shape of the right hand : "fondamentale" / "1er renv." / "2e renv." */
  inversionLabel: string;
  /** Bass MIDI number (already transposed to current tonality). */
  bass: number;
  /** Upper voicing MIDI numbers, sorted ascending. */
  voices: number[];
}

interface VoiceMotion {
  from: number;
  to: number;
  delta: number;
  isCommon: boolean;
}

const VOICE_ROW_HEIGHT = 56;
const BASS_GAP = 32;
const BASS_ROW_HEIGHT = 52;
const COL_WIDTH = 160;
const GAP_WIDTH = 260;
const SCENE_WIDTH = COL_WIDTH * 2 + GAP_WIDTH;
/**
 * Width of the fade mask on each side of the viewport. The viewport is
 * `SCENE_WIDTH + 2 * FADE_WIDTH` wide ; the central `SCENE_WIDTH` is
 * fully opaque and the adjacent scenes peek `FADE_WIDTH` on each side
 * with a linear fade-out.
 */
const FADE_WIDTH = 80;
const VIEWPORT_WIDTH = SCENE_WIDTH + 2 * FADE_WIDTH;
const HEADER_HEIGHT = 100;
const TOTAL_HEIGHT =
  HEADER_HEIGHT + 3 * VOICE_ROW_HEIGHT + BASS_GAP + BASS_ROW_HEIGHT;

interface ProgressionTransitionProps {
  /** Index of the active chord in `slots`. Drives the filmstrip offset. */
  step: number;
  /**
   * All voicings of the progression. Each `slots[i]` is the active
   * voicing at step `i` ; the next chord of scene `i` is `slots[(i+1) % len]`.
   */
  slots: readonly TransitionSlot[];
  /** Use flat note names (B♭ instead of A♯), depends on tonality. */
  useFlats?: boolean;
  /**
   * MIDI numbers held by the student (octave-strict). Drives the
   * per-pastille live highlight on the *active* scene only.
   */
  pressedMidis?: ReadonlySet<number>;
  /** True when the full current voicing (voices + bass) is held. */
  complete?: boolean;
  /**
   * Hold has lasted `HOLD_MS` and we are now inside the confirmation
   * window, drives the wave + progress bar + ✓ badge.
   */
  confirming?: boolean;
}

/**
 * Slide window, must match `AUTO_ADVANCE_TIMINGS.TRANSITION_TOTAL_MS`.
 * The view drives the animation autonomously from a snapshot of the
 * previous slots ; the state machine just locks input for the same
 * duration.
 */
const SLIDE_MS = AUTO_ADVANCE_TIMINGS.TRANSITION_TOTAL_MS;

/**
 * Visualises voice leading across an entire progression as a filmstrip :
 * one `SceneFragment` per chord transition, scrolled horizontally via a
 * CSS `transform` transition. CSS interpolation handles the direction
 * automatically (forward → translates left, backward → translates right)
 *, no per-slide reconciliation, no off-screen snap.
 *
 * Loop wraparound (step `n-1` → `0` or `0` → `n-1`) and multi-step jumps
 * (MiniStrip click) snap without animation, since a long backward
 * scroll wouldn't carry useful pedagogical information.
 */
export function ProgressionTransition({
  step,
  slots,
  useFlats = false,
  pressedMidis,
  complete = false,
  confirming = false,
}: ProgressionTransitionProps) {
  const noteNotation = useNoteNotation();

  const reducedMotion = useReducedMotion();
  const len = slots.length;
  const currentSlot = slots[step]!;
  const nextSlot = slots[(step + 1) % len]!;

  // Disable the CSS transition for one frame on non-adjacent step
  // changes (loop wraparound, MiniStrip jumps) so the offset snaps
  // instead of scrolling the long way through the strip.
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const prevStepRef = useRef(step);

  useEffect(() => {
    const prev = prevStepRef.current;
    prevStepRef.current = step;

    const adjacent = Math.abs(step - prev) <= 1;

    if (adjacent) {
      return;
    }

    setTransitionEnabled(false);

    // Two RAFs : the first lets React commit the new transform with
    // `transition: none`, the second re-enables transitions for the
    // next change. One RAF is sometimes too short on Chromium.
    let pending = 0;
    const id1 = requestAnimationFrame(() => {
      pending = requestAnimationFrame(() => {
        setTransitionEnabled(true);
      });
    });
    pending = id1;

    return () => {
      cancelAnimationFrame(pending);
    };
  }, [step]);

  const narrative = useMemo(() => {
    const motions: VoiceMotion[] = [];
    const n = Math.min(currentSlot.voices.length, nextSlot.voices.length);

    for (let i = 0; i < n; i++) {
      const from = currentSlot.voices[i]!;
      const to = nextSlot.voices[i]!;
      const delta = to - from;
      motions.push({ from, to, delta, isCommon: delta === 0 });
    }

    return buildNarrative(nextSlot, motions, useFlats, noteNotation);
  }, [currentSlot, nextSlot, useFlats, noteNotation]);

  // Filmstrip offset : the active scene's left edge sits at viewport-x
  // = FADE_WIDTH (so it's centred in the SCENE_WIDTH-sized opaque band).
  const offsetPx = FADE_WIDTH - step * SCENE_WIDTH;
  // `cubic-bezier(0.22, 1, 0.36, 1)` is the «&nbsp;easeOutQuart&nbsp;»
  // curve : a strong initial velocity that smoothly decelerates,
  // mimicking a flick gesture. Feels more natural than an in-out curve
  // for a slide that follows the student's intent (advancing to the
  // next chord), the eye never has to wait at the start.
  const transitionStyle =
    !transitionEnabled || reducedMotion
      ? "none"
      : `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;

  // Linear-gradient mask to fade the peeking adjacent scenes.
  const maskImage = `linear-gradient(to right, transparent 0, black ${FADE_WIDTH}px, black calc(100% - ${FADE_WIDTH}px), transparent 100%)`;

  return (
    // `justify-content: safe center` keeps the filmstrip vertically
    // centered when there is room and falls back to `start` (no
    // clipping) when the container is shorter than the content. Paired
    // with `overflow-y: auto`, the user can scroll inside the harmony
    // pane instead of seeing the filmstrip leak under the studio piano.
    <div
      className="relative z-10 flex flex-1 flex-col items-center"
      style={{
        padding: "20px 40px",
        minHeight: 0,
        gap: 22,
        justifyContent: "safe center",
        overflowY: "auto",
      }}
    >
      <ProgressionTransitionStyles />
      <p
        style={{
          margin: 0,
          fontFamily: "Inter, sans-serif",
          fontSize: 13,
          color: TEXT_DIM,
          lineHeight: 1.55,
          maxWidth: 720,
          textAlign: "center",
        }}
      >
        {narrative}
      </p>

      <div
        style={{
          position: "relative",
          width: VIEWPORT_WIDTH,
          height: TOTAL_HEIGHT,
        }}
      >
        <div
          style={{
            overflow: "hidden",
            width: "100%",
            height: TOTAL_HEIGHT,
            maskImage,
            WebkitMaskImage: maskImage,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              height: TOTAL_HEIGHT,
              // `translate3d` forces a GPU compositing layer (some
              // browsers may not promote a 2D `translateX` until the
              // animation starts, the resulting first frame can show
              // a subtle non-accelerated paint).
              transform: `translate3d(${offsetPx}px, 0, 0)`,
              transition: transitionStyle,
              willChange: "transform",
              backfaceVisibility: "hidden",
            }}
          >
            {slots.map((slot, i) => {
              const sceneNext = slots[(i + 1) % len]!;
              const isActive = i === step;

              return (
                <SceneFragment
                  key={i}
                  current={slot}
                  next={sceneNext}
                  useFlats={useFlats}
                  pressedMidis={isActive ? pressedMidis : undefined}
                  complete={isActive && complete}
                  confirming={isActive && confirming}
                  isCycle={isActive && i === len - 1}
                  reducedMotion={reducedMotion}
                />
              );
            })}
          </div>
        </div>

        {confirming ? <ConfirmProgressBar /> : null}
      </div>
    </div>
  );
}

// ─── Scene fragment ────────────────────────────────────────────────────

/**
 * Self-contained `[left chord stack | SVG voice-leading | right chord stack]`
 * unit. All scenes of the progression are pre-mounted side by side in
 * the filmstrip ; only the active one carries live props. Memoised so
 * inactive scenes don't re-render on every press/release of the active
 * scene's keyboard input.
 */
const SceneFragment = memo(function SceneFragmentImpl({
  current,
  next,
  useFlats,
  pressedMidis,
  complete = false,
  confirming = false,
  isCycle = false,
  reducedMotion,
}: {
  current: TransitionSlot;
  next: TransitionSlot;
  useFlats: boolean;
  pressedMidis?: ReadonlySet<number>;
  complete?: boolean;
  confirming?: boolean;
  isCycle?: boolean;
  reducedMotion: boolean;
}) {
  const voiceMotions = useMemo<VoiceMotion[]>(() => {
    const motions: VoiceMotion[] = [];
    const n = Math.min(current.voices.length, next.voices.length);

    for (let i = 0; i < n; i++) {
      const from = current.voices[i]!;
      const to = next.voices[i]!;
      const delta = to - from;
      motions.push({ from, to, delta, isCommon: delta === 0 });
    }

    return motions;
  }, [current.voices, next.voices]);

  const bassDelta = next.bass - current.bass;

  const commonNextMidis = useMemo<ReadonlySet<number>>(() => {
    const set = new Set<number>();
    const n = Math.min(current.voices.length, next.voices.length);

    for (let i = 0; i < n; i++) {
      if (current.voices[i] === next.voices[i]) {
        set.add(next.voices[i]!);
      }
    }

    if (current.bass === next.bass) {
      set.add(next.bass);
    }

    return set;
  }, [current.voices, current.bass, next.voices, next.bass]);

  const bassPressed = pressedMidis?.has(current.bass) ?? false;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        width: SCENE_WIDTH,
        height: TOTAL_HEIGHT,
        flexShrink: 0,
      }}
    >
      <ChordStack
        slot={current}
        side="left"
        useFlats={useFlats}
        pressedMidis={pressedMidis}
        complete={complete}
        confirming={confirming}
        reducedMotion={reducedMotion}
      />

      <svg
        width={GAP_WIDTH}
        height={TOTAL_HEIGHT}
        style={{
          pointerEvents: "none",
          overflow: "visible",
          flexShrink: 0,
        }}
      >
        <DirectionalArrow y={HEADER_HEIGHT - 14} isCycle={isCycle} />

        <g
          style={{
            opacity: complete ? 1 : 0.6,
            transition: "opacity 220ms linear",
          }}
        >
          {voiceMotions.map((motion, i) => {
            const visualIndex = voiceMotions.length - 1 - i;
            const y =
              HEADER_HEIGHT +
              visualIndex * VOICE_ROW_HEIGHT +
              VOICE_ROW_HEIGHT / 2;

            return (
              <VoiceConnector
                key={i}
                y={y}
                delta={motion.delta}
                isCommon={motion.isCommon}
              />
            );
          })}

          <BassLabel
            y={HEADER_HEIGHT + 3 * VOICE_ROW_HEIGHT + BASS_GAP / 2}
            boosted={bassPressed}
          />

          <VoiceConnector
            y={
              HEADER_HEIGHT +
              3 * VOICE_ROW_HEIGHT +
              BASS_GAP +
              BASS_ROW_HEIGHT / 2
            }
            delta={bassDelta}
            isCommon={bassDelta === 0}
            isBass
          />
        </g>
      </svg>

      <ChordStack
        slot={next}
        side="right"
        useFlats={useFlats}
        complete={complete}
        commonMidis={commonNextMidis}
        reducedMotion={reducedMotion}
      />
    </div>
  );
});

// ─── Chord stack ───────────────────────────────────────────────────────

function ChordStack({
  slot,
  side,
  useFlats,
  pressedMidis,
  complete = false,
  confirming = false,
  commonMidis,
  reducedMotion = false,
}: {
  slot: TransitionSlot;
  side: "left" | "right";
  useFlats: boolean;
  pressedMidis?: ReadonlySet<number>;
  complete?: boolean;
  confirming?: boolean;
  commonMidis?: ReadonlySet<number>;
  reducedMotion?: boolean;
}) {
  const noteNotation = useNoteNotation();

  const chordLabel = useChordLabel();

  const align: "right" | "left" = side === "left" ? "right" : "left";

  // Halo radial only meaningful on the left column when the chord is
  // complete, gives an «&nbsp;ambient&nbsp;» glow under the held chord.
  const showHalo = side === "left" && complete;

  // Roman numeral receives a slight scale + brighter shadow when the
  // current chord is fully held. During the confirmation window we
  // also play a one-shot wave keyframe (CSS, defined further down).
  const romanShadowAlpha = side === "left" && complete ? "0.55" : "0.35";
  const romanShadowSize = side === "left" && complete ? "28" : "18";
  const romanScale =
    side === "left" && complete && !reducedMotion ? "scale(1.04)" : "scale(1)";

  return (
    <div
      style={{
        width: COL_WIDTH,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {showHalo ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: -20,
            zIndex: -1,
            pointerEvents: "none",
            background:
              "radial-gradient(ellipse at 50% 60%, rgba(139,92,246,0.18) 0%, transparent 60%)",
            opacity: 1,
            transition: "opacity 220ms linear",
          }}
        />
      ) : null}

      <div
        style={{
          height: HEADER_HEIGHT,
          display: "flex",
          flexDirection: "column",
          alignItems: align === "right" ? "flex-end" : "flex-start",
          justifyContent: "flex-end",
          paddingBottom: 8,
          paddingRight: align === "right" ? 16 : 0,
          paddingLeft: align === "left" ? 16 : 0,
          gap: 2,
          position: "relative",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <div
            // The wave class is only attached on the left column while
            // the auto-advance machine is in `confirming`, the
            // animation runs once (no infinite loop) and is driven by
            // the CSS keyframe defined at the bottom of the file.
            className={
              side === "left" && confirming && !reducedMotion
                ? "harmony-confirm-wave"
                : undefined
            }
            style={{
              fontSize: 44,
              fontFamily: FONT_MONO,
              fontWeight: 600,
              color: slot.functionColor,
              letterSpacing: "0.02em",
              lineHeight: 1,
              textShadow: `0 0 ${romanShadowSize}px ${slot.functionColor.replace(
                "0.85",
                romanShadowAlpha,
              )}`,
              transform: romanScale,
              transition:
                "text-shadow 220ms linear, transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
              willChange: "transform",
            }}
          >
            {slot.roman}
          </div>
          {side === "left" && confirming ? <ConfirmBadge /> : null}
        </div>
        <div
          style={{
            fontSize: 13,
            fontFamily: "Inter, sans-serif",
            fontWeight: 400,
            color: TEXT_DIM,
            letterSpacing: "0",
            lineHeight: 1.2,
          }}
        >
          {chordLabel(slot.symbol)}
        </div>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 500,
            color: TEXT_MUTE,
            letterSpacing: "0.06em",
            marginTop: 2,
          }}
        >
          {slot.inversionLabel}
        </div>
      </div>

      {slot.voices
        .slice()
        .reverse()
        .map((midi, j) => {
          // `slot.voices` is sorted ascending and rendered top-down by
          // pitch, so we must look up the press state by MIDI, not by
          // index.
          const pressed = pressedMidis?.has(midi) ?? false;
          const commonGlow =
            side === "right" && complete && (commonMidis?.has(midi) ?? false);

          return (
            <NoteCell
              key={j}
              height={VOICE_ROW_HEIGHT}
              label={midiToNoteLabel(midi, useFlats, noteNotation)}
              align={align}
              pressed={pressed}
              commonGlow={commonGlow}
              reducedMotion={reducedMotion}
            />
          );
        })}

      <div style={{ height: BASS_GAP }} aria-hidden="true" />

      <NoteCell
        height={BASS_ROW_HEIGHT}
        label={midiToNoteLabel(slot.bass, useFlats, noteNotation)}
        align={align}
        isBass
        pressed={pressedMidis?.has(slot.bass) ?? false}
        commonGlow={
          side === "right" && complete && (commonMidis?.has(slot.bass) ?? false)
        }
        reducedMotion={reducedMotion}
      />

    </div>
  );
}

function NoteCell({
  height,
  label,
  align,
  isBass,
  pressed = false,
  commonGlow = false,
  reducedMotion = false,
}: {
  height: number;
  label: string;
  align: "left" | "right";
  isBass?: boolean;
  /** True when the underlying MIDI note is currently held. */
  pressed?: boolean;
  /**
   * Right-column-only : the note is shared with the current chord and
   * the current chord is fully held. Renders a soft glow without
   * changing the colour or scale.
   */
  commonGlow?: boolean;
  reducedMotion?: boolean;
}) {
  const baseColor = isBass ? TEXT_DIM : TEXT;

  let textShadow: string | undefined;

  if (pressed) {
    textShadow =
      "0 0 8px rgba(196,181,253,0.55), 0 0 18px rgba(139,92,246,0.35)";
  } else if (commonGlow) {
    textShadow = "0 0 6px rgba(196,181,253,0.25)";
  }

  return (
    <div
      style={{
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        padding: align === "right" ? "0 16px 0 0" : "0 0 0 16px",
        fontFamily: FONT_MONO,
        fontSize: isBass ? 16 : 20,
        fontWeight: 500,
        color: baseColor,
        letterSpacing: "0.04em",
        position: "relative",
      }}
    >
      <span
        style={{
          color: pressed ? "#fff" : baseColor,
          textShadow,
          transform:
            pressed && !reducedMotion ? "scale(1.06)" : "scale(1)",
          transformOrigin: align === "right" ? "right center" : "left center",
          transition:
            "transform 180ms cubic-bezier(0.16, 1, 0.3, 1), text-shadow 120ms linear, color 120ms linear",
          display: "inline-block",
          willChange: "transform",
        }}
      >
        {label}
      </span>
      {pressed ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            // Sit 8px from the *inner* edge of the cell. The cell's
            // padding already places the label at 16px from the edge,
            // so the dot lands at 8px which is between the label and
            // the cell border.
            [align === "right" ? "right" : "left"]: 8,
            width: 4,
            height: 4,
            borderRadius: 99,
            background: VIOLET_BRIGHT,
            boxShadow: "0 0 6px rgba(196,181,253,0.7)",
          }}
        />
      ) : null}
    </div>
  );
}

// ─── Directional arrow & bass label ──────────────────────────────────

function DirectionalArrow({
  y,
  isCycle = false,
}: {
  y: number;
  isCycle?: boolean;
}) {
  const padding = 16;
  const x1 = padding;
  const x2 = GAP_WIDTH - padding;
  const stroke = "rgba(196,181,253,0.55)";

  return (
    <g>
      <line
        x1={x1}
        y1={y}
        x2={x2 - 8}
        y2={y}
        stroke={stroke}
        strokeWidth={1.5}
      />
      <polyline
        points={`${x2 - 10},${y - 5} ${x2},${y} ${x2 - 10},${y + 5}`}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x={GAP_WIDTH / 2}
        y={y - 8}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="9"
        fontWeight="600"
        fill={VIOLET_BRIGHT}
        letterSpacing="0.2em"
        opacity={0.7}
      >
        {isCycle ? "↻ CYCLE" : "TRANSITION"}
      </text>
    </g>
  );
}

function BassLabel({
  y,
  boosted = false,
}: {
  y: number;
  /** Bumps the label opacity by ~+0.15 when the bass MIDI is held. */
  boosted?: boolean;
}) {
  return (
    <g>
      <text
        x={GAP_WIDTH / 2}
        y={y + 4}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="9"
        fontWeight="600"
        fill={boosted ? TEXT_DIM : TEXT_MUTE}
        letterSpacing="0.18em"
        style={{ transition: "fill 120ms linear" }}
      >
        BASSE · MAIN GAUCHE
      </text>
    </g>
  );
}

// ─── Voice connector (SVG) ────────────────────────────────────────────

function VoiceConnector({
  y,
  delta,
  isCommon,
  isBass,
}: {
  y: number;
  delta: number;
  isCommon: boolean;
  isBass?: boolean;
}) {
  if (isCommon) {
    return (
      <g>
        <line
          x1={0}
          y1={y}
          x2={GAP_WIDTH}
          y2={y}
          stroke={VIOLET_BRIGHT}
          strokeWidth={2.5}
        />
        <line
          x1={0}
          y1={y}
          x2={GAP_WIDTH}
          y2={y}
          stroke={VIOLET_GLOW}
          strokeWidth={6}
          opacity={0.4}
        />
        <text
          x={GAP_WIDTH / 2}
          y={y - 10}
          textAnchor="middle"
          fontFamily="ui-monospace, monospace"
          fontSize="9"
          fontWeight="600"
          fill={VIOLET_BRIGHT}
          letterSpacing="0.16em"
        >
          {isBass ? "BASSE STABLE" : "RESTE"}
        </text>
      </g>
    );
  }

  const arrow = delta > 0 ? "↑" : "↓";
  const semis = Math.abs(delta);
  let intervalLabel: string;

  if (isBass) {
    intervalLabel = `${delta > 0 ? "+" : "−"}${semis}`;
  } else if (semis === 1) {
    intervalLabel = `${arrow} ½ ton`;
  } else if (semis === 2) {
    intervalLabel = `${arrow} 1 ton`;
  } else {
    intervalLabel = `${arrow} ${semis} demi-tons`;
  }

  const stroke = isBass ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.28)";
  const labelColor = isBass ? TEXT_MUTE : TEXT_DIM;

  return (
    <g>
      <line
        x1={0}
        y1={y}
        x2={GAP_WIDTH}
        y2={y}
        stroke={stroke}
        strokeWidth={1}
        strokeDasharray="4 4"
      />
      <text
        x={GAP_WIDTH / 2}
        y={y - 10}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize={isBass ? "10" : "11"}
        fontWeight="600"
        fill={labelColor}
        letterSpacing="0.08em"
      >
        {intervalLabel}
      </text>
    </g>
  );
}

// ─── Narrative ────────────────────────────────────────────────────────

/**
 * Bare interval label («&nbsp;½ ton&nbsp;», «&nbsp;1 ton&nbsp;»,
 * «&nbsp;3 demi-tons&nbsp;»…). No direction arrow.
 */
function formatIntervalSize(delta: number): string {
  const semis = Math.abs(delta);

  if (semis === 1) {
    return "½ ton";
  }

  if (semis === 2) {
    return "1 ton";
  }

  return `${semis} demi-tons`;
}

/**
 * Format a single voice move as «&nbsp;C ↑ ½ ton D&nbsp;» (or descending).
 * Reused by both the «&nbsp;tout glisse&nbsp;» and «&nbsp;une seule
 * voix bouge&nbsp;» branches of the narrative.
 */
function formatMove(
  motion: VoiceMotion,
  useFlats: boolean,
  notation: NoteNotation,
): string {
  const from = midiToPitchClass(motion.from, useFlats, notation);
  const to = midiToPitchClass(motion.to, useFlats, notation);
  const arrow = motion.delta > 0 ? "↑" : "↓";

  return `${from} ${arrow} ${formatIntervalSize(motion.delta)} ${to}`;
}

/**
 * Interpretative narrative for the current → next transition. The goal
 * here is *not* to describe the move (the SVG already does that) but to
 * tell the student what the move *means* in terms of hand economy :
 *
 * - everything stays → nothing moves, the hand is frozen
 * - 2 or 3 voices stay → the ideal voicing, only one finger glides
 * - 1 voice stays → an anchor note while the others move
 * - no common notes, all small steps → smooth glide, still light
 * - no common notes, big jumps → the hand has to travel
 *
 * Bass motion is intentionally excluded ; it always changes with the
 * chord root and is shown separately in the SVG. The narrative focuses
 * on the right-hand voicing choices, which is where the inversion
 * decision pays off.
 */
function buildNarrative(
  next: TransitionSlot,
  motions: VoiceMotion[],
  useFlats: boolean,
  notation: NoteNotation,
): string {
  const common = motions.filter((m) => m.isCommon);
  const moves = motions.filter((m) => !m.isCommon);
  const total = motions.length;
  const stays = common
    .map((m) => midiToPitchClass(m.from, useFlats, notation))
    .join(", ");
  const movePieces = moves.map((m) => formatMove(m, useFlats, notation));

  if (total === 0) {
    return `Vers ${next.roman}.`;
  }

  // Cas 1 : aucune voix ne bouge (rare, accord répété).
  if (common.length === total) {
    return `Vers ${next.roman}, aucune note ne bouge. La main reste figée — tu peux ne rien changer.`;
  }

  // Cas 2 : ≥ 2 voix sur 3 restent en place.
  if (common.length >= 2 && moves.length === 1) {
    const m = moves[0]!;
    const semis = Math.abs(m.delta);

    if (semis <= 2) {
      return `Vers ${next.roman}, ${stays} ne bougent pas. Une seule voix glisse d'${semis === 1 ? "un demi-ton" : "un ton"}. C'est l'idéal d'un bon voicing.`;
    }

    return `Vers ${next.roman}, ${stays} ne bougent pas. Une voix saute (${movePieces[0]}) — un effort, mais minimal.`;
  }

  // Cas 3 : une seule voix tient l'ancre.
  if (common.length === 1) {
    const allStep = moves.every((m) => Math.abs(m.delta) <= 2);

    if (allStep) {
      return `Vers ${next.roman}, ${stays} reste accrochée. Les autres voix glissent à peine — ${movePieces.join(" · ")}.`;
    }

    return `Vers ${next.roman}, ${stays} reste, mais les autres voix bougent franchement — ${movePieces.join(" · ")}.`;
  }

  // Cas 4 : aucune note commune.
  const allStep = moves.every((m) => Math.abs(m.delta) <= 2);

  if (allStep) {
    return `Vers ${next.roman}, aucune note commune mais tout glisse en douceur — chaque voix bouge d'un demi-ton ou d'un ton (${movePieces.join(" · ")}).`;
  }

  return `Vers ${next.roman}, aucune note commune et toutes les voix sautent ensemble (${movePieces.join(" · ")}). La main voyage — c'est le signe d'un voicing à repenser.`;
}

// ─── Confirmation visuals (auto-advance) ─────────────────────────────

/**
 * Small ✓ badge shown next to the roman numeral while the auto-advance
 * machine is in `confirming`. Fades out during the last 200 ms of the
 * 400 ms confirmation window via the `harmony-confirm-badge` keyframe.
 */
function ConfirmBadge() {
  return (
    <span
      aria-hidden="true"
      className="harmony-confirm-badge"
      style={{
        width: 14,
        height: 14,
        borderRadius: 99,
        background: VIOLET_BRIGHT,
        color: "#0a0a0e",
        fontFamily: FONT_MONO,
        fontSize: 10,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      ✓
    </span>
  );
}

/**
 * 2px progress bar centered under the whole transition row, growing
 * from the center outwards over the 400 ms confirmation window.
 * Non-decorative cue, stays enabled even under `prefers-reduced-motion`.
 */
const CONFIRM_BAR_WIDTH = 360;

function ConfirmProgressBar() {
  return (
    <div
      aria-hidden="true"
      className="harmony-confirm-bar"
      style={{
        position: "absolute",
        bottom: -14,
        left: "50%",
        marginLeft: -CONFIRM_BAR_WIDTH / 2,
        width: CONFIRM_BAR_WIDTH,
        height: 2,
        borderRadius: 99,
        background: VIOLET_BRIGHT,
        boxShadow: "0 0 8px rgba(196,181,253,0.4)",
        transformOrigin: "center center",
        transform: "scaleX(0)",
      }}
    />
  );
}

/**
 * Single source of truth for the keyframes used by the auto-advance
 * visuals. Inlined here so the file remains self-contained, the
 * harmony tab is the only consumer.
 *
 * Note about `prefers-reduced-motion` : the filmstrip transition is
 * disabled (transform changes apply instantly), so step changes snap
 * without animation. The progress bar's `scaleX` is intentionally
 * preserved because it carries time-to-advance information.
 */
function ProgressionTransitionStyles() {
  return (
    <style>{`
      @keyframes harmony-confirm-wave {
        0%   { transform: scale(1.04); }
        50%  { transform: scale(1.10); }
        100% { transform: scale(1.00); }
      }
      .harmony-confirm-wave {
        animation: harmony-confirm-wave 700ms cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      @keyframes harmony-confirm-badge {
        0%   { opacity: 1; }
        50%  { opacity: 1; }
        100% { opacity: 0; }
      }
      .harmony-confirm-badge {
        animation: harmony-confirm-badge 700ms linear forwards;
      }

      @keyframes harmony-confirm-bar {
        from { transform: scaleX(0); }
        to   { transform: scaleX(1); }
      }
      .harmony-confirm-bar {
        animation: harmony-confirm-bar 700ms linear forwards;
      }

    `}</style>
  );
}
